import React, { useId } from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { getMessageSenderName } from 'flow/pages/messages/MessageSender';
import { getMessageSentAriaLabel } from 'flow/utils/message-row-utils';
import { selectChatMessageByKey } from 'flow/selectors/chat-message-selectors';
import { ChatMessageKey, ChatSessionKey } from 'flow/types/EntityKeys';
import ChatPostDisplay from 'flow/pages/messages/ChatPostDisplay';
import ChatPostFooter from 'flow/pages/messages/ChatPostFooter';

interface Props {
    chatSessionKey: ChatSessionKey;
    chatMessageKey: ChatMessageKey;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const ChatPostRow = ({
    chatSessionKey,
    chatMessageKey,
    videoDark = false,
}: Props) => {
    const message = useAppSelector((state) =>
        selectChatMessageByKey(state, chatSessionKey, chatMessageKey),
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { i18next } = useAppContext();
    const bodyId = `${useId()}-body`;

    if (!message) return null;

    const senderName = getMessageSenderName(message, ownExtensionKey, i18next);
    const sentLabel = getMessageSentAriaLabel(message.get('sent'));

    return (
        <article
            data-message-row=""
            tabIndex={-1}
            aria-label={`${senderName}, ${sentLabel}`}
            aria-describedby={bodyId}
            className="focus-ring-inset w-full scroll-mt-10 rounded-sm p-4"
        >
            <div
                className={twMerge(
                    'rounded-sm shadow-lg',
                    videoDark
                        ? 'border border-gray-600 bg-gray-700'
                        : 'bg-white dark:bg-gray-100',
                )}
            >
                <div id={bodyId}>
                    <ChatPostDisplay
                        message={message}
                        sessionKey={chatSessionKey}
                        isSummary={true}
                        videoDark={videoDark}
                    />
                </div>
                <ChatPostFooter
                    message={message}
                    sessionKey={chatSessionKey}
                    navigateOnClick={true}
                    videoDark={videoDark}
                />
            </div>
        </article>
    );
};

export default React.memo(ChatPostRow);
